import type { Schemas } from "@norn/sdk"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { Check, X } from "lucide-react"
import { type KeyboardEvent, type ReactNode, useMemo, useState } from "react"
import { Kbd } from "@/components/ui/kbd"
import { refusalOfError, run } from "@/lib/api"
import type { Scope } from "@/lib/connection"
import { useT } from "@/lib/i18n"
import { parseExact, stringifyExact } from "@/lib/lossless"
import { useCell } from "@/lib/session"
import { cn } from "@/lib/utils"
import { useLinks, useObjectsByKey } from "@/workspace/hooks"
import { RefusalNote } from "@/workspace/values"
import { type Decision, TYPES } from "./data"
import { useWorkbookDates } from "./dates"
import { decisionKey, type ErpRow, needsReview, type Pair } from "./match"
import { PendingVerdicts } from "./pending"
import { ConfidenceBar, CustomerMark, ReasonPill, useWhy } from "./parts"

export const DECIDE_ACTION = "recon.order_match.decide"

/** An earlier verdict on this pair has an unknown outcome; it must be retried before the other one. */
export class UnresolvedVerdict extends Error {
  constructor(readonly outcome: "accepted" | "rejected") {
    super(`The earlier ${outcome} verdict on this pair has no confirmed result yet; retry it first.`)
  }
}

type Accepted = Schemas["ActionAccepted"]
type Outcome = "accepted" | "rejected"

/** What a workbook's review queue needs to know about its items: how to key, label, show and decide them. */
export interface DecisionConfig<T> {
  /** The authorized action that records a verdict. */
  readonly action: string
  /** The item's stable id in the queue. */
  readonly idOf: (item: T) => string
  /** The decision record's key: the item at the versions the reviewer saw. */
  readonly keyOf: (item: T) => string
  /** The action input for a verdict on this item. */
  readonly inputOf: (item: T, outcome: Outcome) => Record<string, unknown>
  /** One line naming the item in the queue and in its receipt. */
  readonly labelOf: (item: T) => string
  /** The item's verdict pill and confidence, under its label. */
  readonly markOf: (item: T) => ReactNode
  /** The item's detail beside the queue: what the rules saw, side by side. */
  readonly detailOf: (item: T) => ReactNode
  readonly acceptLabel: string
  readonly queueLabel: string
  readonly emptyLabel: string
}

/** Decide one item through the cell's authorized action; the receipt comes back from the ledger. */
export function useDecide<T>(scope: Scope, actor: string | null, decisions: ReadonlyMap<string, Decision>, config: Pick<DecisionConfig<T>, "action" | "keyOf" | "inputOf">) {
  const { connection, client } = useCell()
  const queryClient = useQueryClient()
  // One idempotency key per pending submission on a decision: a retry after a lost response replays
  // the same commit. Until the cell confirms it (a response, or a newer decision record with that
  // outcome), the other verdict waits; the guard lives in session storage, not in this component.
  const pending = useMemo(() => new PendingVerdicts(connection.baseUrl, scope), [connection.baseUrl, scope])
  return useMutation({
    mutationFn: async ({ item, outcome }: { item: T; outcome: Outcome }) => {
      if (!actor) throw new Error("No signed-in principal")
      const key = config.keyOf(item)
      const input = config.inputOf(item, outcome)
      const open = pending.open(key, decisions.get(key))
      if (open && open.outcome !== outcome) throw new UnresolvedVerdict(open.outcome)
      // A retry resends the frozen request byte for byte under its original key.
      const body = open?.body ?? stringifyExact({ scope: { orgId: scope.orgId, environmentId: scope.environmentId }, actor, input, idempotencyKey: crypto.randomUUID() })
      pending.set(key, { outcome, body, baseVersion: open?.baseVersion ?? decisions.get(key)?.version ?? 0 })
      const text = await run(connection.baseUrl, () =>
        client.POST("/v1/actions/{action}", { params: { path: { action: config.action } }, body: {} as never, bodySerializer: () => body, parseAs: "text" }),
      )
      return { item, outcome, accepted: parseExact(text) as Accepted }
    },
    onSuccess: ({ item, outcome }) => {
      pending.retire(config.keyOf(item), outcome)
      void queryClient.invalidateQueries({ queryKey: ["wb-all"] })
    },
    // A refusal is a definite answer (nothing committed): the attempt resolves and its key retires.
    // Only an unreachable cell or a transport failure leaves the outcome unknown.
    onError: (error, { item, outcome }) => {
      if (refusalOfError(error)) pending.retire(config.keyOf(item), outcome)
    },
  })
}

/**
 * A workbook's review queue: every item a person should judge, in the order given. ↑ ↓ (or j k)
 * move, A accepts, R rejects; each verdict is an authorized action whose ledger receipt shows
 * beside the item.
 */
export function DecisionQueue<T>({ scope, items, decisions, actor, config }: { scope: Scope; items: readonly T[]; decisions: ReadonlyMap<string, Decision>; actor: string | null; config: DecisionConfig<T> }) {
  const t = useT()
  const [showDecided, setShowDecided] = useState(false)
  const queue = useMemo(() => items.filter((p) => showDecided || !decisions.has(config.keyOf(p))), [items, decisions, showDecided, config])
  const [activeId, setActiveId] = useState<string | null>(null)
  const active = queue.find((p) => config.idOf(p) === activeId) ?? queue[0] ?? null
  const decide = useDecide(scope, actor, decisions, config)
  const [receipts, setReceipts] = useState<ReadonlyMap<string, { outcome: string; seq: number; replayed: boolean }>>(new Map())
  // The queue moves on after a verdict; the last receipt stays in view until the next one.
  const [last, setLast] = useState<{ label: string; outcome: string; seq: number; replayed: boolean } | null>(null)

  const commit = (outcome: Outcome) => {
    if (!active || decide.isPending) return
    const at = queue.indexOf(active)
    decide.mutate(
      { item: active, outcome },
      {
        onSuccess: ({ item, accepted }) => {
          const receipt = { outcome, seq: Number(accepted.receipt.seq), replayed: accepted.replayed }
          setReceipts((r) => new Map(r).set(config.keyOf(item), receipt))
          setLast({ label: config.labelOf(item), ...receipt })
          // Move on to the next undecided item.
          const next = queue[at + 1] ?? queue[at - 1]
          if (next && !showDecided) setActiveId(config.idOf(next))
        },
      },
    )
  }
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLButtonElement && (e.key === "Enter" || e.key === " ")) return
    const at = active ? queue.indexOf(active) : -1
    if (e.key === "ArrowDown" || e.key === "j") {
      e.preventDefault()
      const next = queue[Math.min(queue.length - 1, at + 1)]
      if (next) setActiveId(config.idOf(next))
    } else if (e.key === "ArrowUp" || e.key === "k") {
      e.preventDefault()
      const prev = queue[Math.max(0, at - 1)]
      if (prev) setActiveId(config.idOf(prev))
    } else if (e.key === "a") commit("accepted")
    else if (e.key === "r") commit("rejected")
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>
          {t("wb.review.keys")} <Kbd>↑</Kbd> <Kbd>↓</Kbd> · <Kbd>A</Kbd> {t("wb.review.accept")} · <Kbd>R</Kbd> {t("wb.review.reject")}
        </span>
        {last ? (
          <span role="status" className="text-success">
            {last.label} · {t(last.outcome === "accepted" ? "wb.review.recordedAccepted" : "wb.review.recordedRejected", { seq: String(last.seq) })}
            {last.replayed ? ` · ${t("wb.review.replayed")}` : ""}
          </span>
        ) : null}
        <label className="ms-auto inline-flex items-center gap-1.5">
          <input type="checkbox" checked={showDecided} onChange={(e) => setShowDecided(e.target.checked)} className="size-3.5" />
          {t("wb.review.showDecided")}
        </label>
      </div>
      {queue.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">{config.emptyLabel}</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <div role="listbox" aria-label={config.queueLabel} tabIndex={0} onKeyDown={onKey} className="max-h-[28rem] overflow-y-auto rounded-xl border border-border bg-card outline-none focus-visible:ring-3 focus-visible:ring-ring/30">
            {queue.map((p) => {
              const id = config.idOf(p)
              const decided = receipts.get(config.keyOf(p))?.outcome ?? decisions.get(config.keyOf(p))?.outcome
              return (
                <button
                  key={id}
                  type="button"
                  role="option"
                  aria-selected={active !== null && id === config.idOf(active)}
                  tabIndex={-1}
                  onClick={() => setActiveId(id)}
                  className={cn(
                    "grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b border-border/70 px-3 py-2 text-start text-[13px] last:border-b-0 hover:bg-muted/40",
                    active !== null && id === config.idOf(active) && "bg-[var(--lineage)]/10",
                  )}
                >
                  <span className="min-w-0">
                    <span dir="auto" className="block truncate font-medium">
                      {config.labelOf(p)}
                    </span>
                    <span className="mt-0.5 flex items-center gap-2">{config.markOf(p)}</span>
                  </span>
                  {decided ? (
                    <span className={cn("text-2xs font-medium", decided === "accepted" ? "text-success" : "text-destructive")}>{decided === "accepted" ? t("wb.review.accepted") : t("wb.review.rejected")}</span>
                  ) : null}
                </button>
              )
            })}
          </div>
          {active ? (
            <DecisionDetail
              key={config.idOf(active)}
              scope={scope}
              decided={receipts.get(config.keyOf(active))?.outcome ?? decisions.get(config.keyOf(active))?.outcome ?? null}
              receipt={receipts.get(config.keyOf(active)) ?? null}
              pending={decide.isPending}
              error={decide.isError && decide.variables && config.idOf(decide.variables.item) === config.idOf(active) ? decide.error : null}
              canAct={Boolean(actor)}
              acceptLabel={config.acceptLabel}
              onDecide={commit}
            >
              {config.detailOf(active)}
            </DecisionDetail>
          ) : null}
        </div>
      )}
    </div>
  )
}

/** The order match's review queue: every pair a person should judge, least certain first. */
export function ReviewQueue({ scope, pairs, decisions, actor }: { scope: Scope; pairs: readonly Pair[]; decisions: ReadonlyMap<string, Decision>; actor: string | null }) {
  const t = useT()
  const config = useMemo<DecisionConfig<Pair>>(
    () => ({
      action: DECIDE_ACTION,
      idOf: (p) => p.id,
      keyOf: decisionKey,
      inputOf: (pair, outcome) => ({
        decision_id: decisionKey(pair),
        outcome,
        reason: pair.reason,
        rule: pair.rule,
        confidence: pair.confidence,
        ...(pair.shop ? { shopify_order: pair.shop.key } : {}),
        ...(pair.erp ? { erp_order: pair.erp.key } : {}),
      }),
      labelOf: (p) => `${p.shop?.name ?? "—"} ↔ ${p.erp?.number ?? "—"}`,
      markOf: (p) => (
        <>
          <ReasonPill reason={p.reason} rule={p.rule} review={needsReview(p)} />
          <ConfidenceBar value={p.confidence} />
        </>
      ),
      detailOf: (p) => <PairBody scope={scope} pair={p} />,
      acceptLabel: t("wb.review.acceptPair"),
      queueLabel: t("wb.review.queue"),
      emptyLabel: t("wb.review.empty"),
    }),
    [scope, t],
  )
  return <DecisionQueue scope={scope} items={pairs} decisions={decisions} actor={actor} config={config} />
}

/** The active item's detail, its verdict buttons and its receipt. */
function DecisionDetail({
  scope,
  decided,
  receipt,
  pending,
  error,
  canAct,
  acceptLabel,
  onDecide,
  children,
}: {
  scope: Scope
  decided: string | null
  receipt: { outcome: string; seq: number; replayed: boolean } | null
  pending: boolean
  error: unknown
  canAct: boolean
  acceptLabel: string
  onDecide: (outcome: Outcome) => void
  children: ReactNode
}) {
  const t = useT()
  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-4">
      {children}
      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <button
          type="button"
          disabled={pending || !canAct}
          onClick={() => onDecide("accepted")}
          className="inline-flex h-8 items-center gap-1.5 rounded-full bg-foreground px-3.5 text-xs font-medium text-background outline-none hover:opacity-90 focus-visible:ring-3 focus-visible:ring-ring/30 disabled:opacity-50"
        >
          <Check className="size-3.5" aria-hidden /> {acceptLabel}
        </button>
        <button
          type="button"
          disabled={pending || !canAct}
          onClick={() => onDecide("rejected")}
          className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border px-3.5 text-xs outline-none hover:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/30 disabled:opacity-50"
        >
          <X className="size-3.5" aria-hidden /> {t("wb.review.rejectPair")}
        </button>
        {receipt ? (
          <span role="status" className="text-xs text-success">
            {t(receipt.outcome === "accepted" ? "wb.review.recordedAccepted" : "wb.review.recordedRejected", { seq: String(receipt.seq) })}
            {receipt.replayed ? ` · ${t("wb.review.replayed")}` : ""}
          </span>
        ) : decided ? (
          <span className="text-xs text-muted-foreground">{t(decided === "accepted" ? "wb.review.wasAccepted" : "wb.review.wasRejected")}</span>
        ) : null}
        <Link to="/$orgId/$environmentId/ledger" params={scope} className="ms-auto text-xs text-muted-foreground hover:text-foreground">
          {t("wb.review.ledger")}
        </Link>
      </div>
      {error ? <RefusalNote error={error} title={t("wb.review.refused")} /> : null}
    </div>
  )
}

function PairBody({ scope, pair }: { scope: Scope; pair: Pair }) {
  const t = useT()
  const why = useWhy()
  const dates = useWorkbookDates()
  return (
    <>
      <p className="text-sm">{why(pair)}</p>
      {pair.shop && pair.erp ? (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>
            {t("wb.signal.customer")} <CustomerMark signal={pair.customer} />
          </span>
          <span>
            {t("wb.signal.amount")} <span className="tabular-nums text-foreground">{pair.delta === 0 ? t("wb.signal.same") : (pair.delta ?? "—")}</span>
          </span>
          <span>
            {t("wb.signal.days")} <span className="tabular-nums text-foreground">{pair.dayGap ?? "—"}</span>
          </span>
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Side title={t("wb.side.shopify")}>
          {pair.shop ? (
            <>
              <Row label={t("wb.col.order")}>
                <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: "shopify.Order", key: pair.shop.key }} className="font-medium hover:underline">
                  {pair.shop.name}
                </Link>
              </Row>
              <Row label={t("wb.col.placed")}>
                <span title={pair.shop.createdAt}>{dates.stamp(pair.shop.createdAt)}</span>
              </Row>
              <Row label={t("wb.col.total")}>
                <span className="tabular-nums">
                  {pair.shop.totalText} {pair.shop.currency}
                </span>
              </Row>
              <Row label={t("wb.col.customer")}>{pair.shop.emailHidden ? <span className="text-muted-foreground">{t("wb.hidden")}</span> : (pair.shop.email ?? "—")}</Row>
              <Row label={t("wb.col.reference")}>{pair.shop.reference ?? <span className="text-muted-foreground">{t("wb.noReference")}</span>}</Row>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t("wb.side.noShopify")}</p>
          )}
        </Side>
        <Side title={t("wb.side.erp")}>
          {pair.erp ? (
            <>
              <Row label={t("wb.col.order")}>
                <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: "commerce.SalesOrder", key: pair.erp.key }} className="font-medium hover:underline">
                  {pair.erp.number}
                </Link>
              </Row>
              <Row label={t("wb.col.date")}>
                <span title={pair.erp.date ?? undefined}>{dates.day(pair.erp.date) ?? "—"}</span>
              </Row>
              <Row label={t("wb.col.total")}>
                <span className="tabular-nums">
                  {pair.erp.total ?? "—"} {pair.erp.currency}
                </span>
              </Row>
              <Row label={t("wb.col.customer")}>
                <ErpCustomer scope={scope} erp={pair.erp} />
              </Row>
              <Row label={t("wb.col.email")}>{pair.erp.customerEmailHidden ? <span className="text-muted-foreground">{t("wb.hidden")}</span> : (pair.erp.customerEmail ?? "—")}</Row>
              <Row label={t("wb.col.status")}>{pair.erp.status}</Row>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t("wb.side.noErp")}</p>
          )}
        </Side>
      </div>
      {pair.alternatives.length ? <p className="text-xs text-muted-foreground">{t("wb.review.alternatives", { orders: pair.alternatives.join(", ") })}</p> : null}
    </>
  )
}

export function Side({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5 rounded-lg bg-surface-subtle/60 p-3">
      <p className="text-2xs font-semibold tracking-wide text-muted-foreground uppercase">{title}</p>
      <dl className="space-y-1 text-[13px]">{children}</dl>
    </div>
  )
}

export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd dir="auto" className="min-w-0 truncate">
        {children}
      </dd>
    </div>
  )
}

/**
 * The ERP order's customer: from the sheet's customer join when the source mapping carries the
 * order's customer code, else through the order's counterparty link. Hidden values stay hidden.
 */
function ErpCustomer({ scope, erp }: { scope: Scope; erp: ErpRow }) {
  const t = useT()
  if (erp.customerHidden) return <span className="text-muted-foreground">{t("wb.hidden")}</span>
  if (!erp.customerKey) return <LinkedCustomer scope={scope} orderKey={erp.key} />
  return (
    <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: TYPES.customer, key: erp.customerKey }} className="hover:underline">
      {erp.customer ?? erp.customerKey}
    </Link>
  )
}

function LinkedCustomer({ scope, orderKey }: { scope: Scope; orderKey: string }) {
  const t = useT()
  const links = useLinks(scope, TYPES.erp, orderKey)
  const ref = links.data?.items.find((l) => l.linkId === "counterparty" && l.from.key === orderKey)?.to
  const refs = useMemo(() => (ref ? [{ objectType: ref.objectType, key: ref.key }] : []), [ref])
  const customers = useObjectsByKey(scope, refs)
  const customer = ref ? customers.get(`${ref.objectType}/${ref.key}`)?.data : undefined
  if (links.isPending) return <span className="text-muted-foreground">…</span>
  if (!ref) return <span className="text-muted-foreground">{links.data?.masked && Object.keys(links.data.masked).length ? t("wb.hidden") : "—"}</span>
  const values = (customer?.values ?? {}) as Record<string, unknown>
  const masked = (customer?.masked ?? {}) as Record<string, unknown>
  const name = "display_name" in masked ? t("wb.hidden") : typeof values.display_name === "string" ? values.display_name : null
  return (
    <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: ref.objectType, key: ref.key }} className="hover:underline">
      {name ?? ref.key}
    </Link>
  )
}
